import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Modal, KeenIcon, ModalContent, ModalHeader } from '@/components';
import { AuthContext } from '@/auth/providers/JWTProvider.tsx'; // Pfad ggf. anpassen

const SessionTimer: React.FC = () => {
  const authContext = useContext(AuthContext);
  const logout = authContext?.logout;

  if (!logout) {
    console.error('Abmeldefunktion ist im AuthContext nicht verfügbar.');
    return null;
  }

  const envTime = parseInt(import.meta.env.VITE_SESSION_TIME || '900', 10);
  const INITIAL_TIME = !isNaN(envTime) && envTime > 0 ? envTime : 900;
  const WARNING_TIME = 60; // 1 Minute in Sekunden

  const [secondsLeft, setSecondsLeft] = useState<number>(INITIAL_TIME);
  const [showModal, setShowModal] = useState<boolean>(false);

  const resetTimer = useCallback(() => {
    setSecondsLeft(INITIAL_TIME);
  }, [INITIAL_TIME]);

  // Countdown wird jede Sekunde reduziert
  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Warn-Modal anzeigen, sobald die Warnzeit erreicht ist
  useEffect(() => {
    if (secondsLeft <= WARNING_TIME && !showModal && secondsLeft > 0) {
      setShowModal(true);
    }
  }, [secondsLeft, showModal]);

  // Automatisches Logout bei Ablauf der Zeit
  useEffect(() => {
    if (secondsLeft <= 0) {
      logout();
    }
  }, [secondsLeft, logout]);

  // Timer bei Benutzeraktivität zurücksetzen
  useEffect(() => {
    const events = ['click', 'keypress', 'scroll'];
    events.forEach((event) => window.addEventListener(event, resetTimer));
    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [resetTimer]);

  const formatTime = (secs: number) => {
    const minutes = Math.floor(Math.max(secs, 0) / 60);
    const seconds = Math.max(secs, 0) % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="text-xs cursor-pointer" onClick={() => setShowModal(true)}>
          Automatische Abmeldung in {Math.ceil(secondsLeft / 60)} Min.
        </div>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <ModalContent className="container-fixed px-30 overflow-hidden pt-7.5 my-[3%] w-full">
          <ModalHeader className="p-0 border-0">
            <div className="flex items-center justify-between flex-wrap grow gap-5">
              <div className="flex items-baseline space-x-2">
                <KeenIcon icon="timer" className="text-3xl" />
                <h1 className="text-xl font-semibold leading-none text-gray-900">
                  Warnung: Sitzungsablauf
                </h1>
              </div>
            </div>
          </ModalHeader>
          <div className="py-4 text-gray-700 text-sm">
            <p>
              Ihre Sitzung läuft in {formatTime(secondsLeft)} ab.
              <br />
              Aus Sicherheitsgründen werden Sie automatisch abgemeldet, sobald die Zeit abgelaufen
              ist. Nicht gespeicherte Daten gehen dabei verloren. Möchten Sie Ihre Sitzung
              fortsetzen?
            </p>
            <div className="flex mt-5 gap-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setShowModal(false);
                  resetTimer();
                }}
              >
                Sitzung fortsetzen
              </button>
              <button
                className="btn btn-sm"
                onClick={() => {
                  setShowModal(false);
                  logout();
                }}
              >
                Abmelden
              </button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export { SessionTimer };
